/**
 * Global overlays (plan §4.1, owner ui-layers): `openLayer(id, props)` opens a registered layer from
 * anywhere. Definitions live in `src/overlays/<name>.tsx` (discovered, light: a lazy component plus an
 * optional tiny `Effect`); the heavy component (`*.layer.tsx` or a feature module) loads on first
 * open, so editors and their dependencies never ship on first paint. Layers stack on the one layer
 * stack of `@dataroom/ui`: one scrim, Escape closes the top layer, route changes close them.
 */
import type { ComponentType } from 'react';
import {
  type AnyGlobalLayerDefinition,
  type GlobalLayerComponentProps,
  type GlobalLayerDefinition,
  createGlobalLayerRegistry,
} from '@dataroom/ui';
import type { ComposeRequest } from '../features/communications/events';
import type { FormulaEditorTarget } from '../features/formula/open';
import type { MergeRequest } from '../features/merge/events';
import type { PresentationRequest } from '../features/present/types';
import type { CreateRecordRequest } from '../features/records/events';
import type { RecordList } from '../features/records/list-context';
import { armPaletteTypeahead } from '../overlays/palette-typeahead';

/**
 * Props of every global layer id. The reserved ids of plan §4.1 are fixed here; owners that add a
 * new id extend this interface from their own module:
 * `declare module '../shell/overlays' { interface GlobalLayers { 'my-id': MyProps } }`.
 */
export interface GlobalLayers {
  /** ⌘K command bar (shell-nav). `query` prefills the search. */
  palette: { query?: string };
  /**
   * Copilot panel (copilot-panel). Until registered: no-op. `prompt` is a question handed over by the
   * ⌘K command bar (`?` or "Ask AI: …"); the panel sends it as the first message.
   */
  copilot: {
    scope?: unknown;
    prompt?: string | undefined;
    builderId?: string;
    newConversation?: string;
    /** Send `prompt` as the first message right away (a creation choice such as "Describe it"). */
    send?: boolean;
  };
  /** Mail composer (mail). */
  composer: ComposeRequest;
  /** Formula editor (data-model-formula). */
  formula: FormulaEditorTarget;
  /** Merge two records (views-filters-bulk). */
  merge: MergeRequest;
  /** Presentation mode for a page or deck (slides-present-export; open with `openPresentation`). */
  presentation: PresentationRequest;
  /**
   * Record peek side sheet (record-runtime). Until registered: navigates to the record page. `list` is
   * the ids the peek steps through; `from` the list they come from (its filter and sort), handed on to
   * the record page "Open full page" opens (default: the object's list on screen when `list` is given).
   */
  'record-peek': { objectKey: string; recordId: string; list?: string[]; from?: RecordList };
  /** Create-record dialog (record-create-mobile). */
  'record-create': CreateRecordRequest;
}

export type GlobalLayerId = keyof GlobalLayers;
export type GlobalLayerProps<K extends GlobalLayerId> = GlobalLayerComponentProps<GlobalLayers[K]>;

const registry = createGlobalLayerRegistry<GlobalLayers>();

/** Open a global layer (or update it when it is already open). */
export function openLayer<K extends GlobalLayerId>(id: K, props: GlobalLayers[K]): void {
  // The command bar mounts a frame later: what is typed meanwhile is kept for its input.
  if (id === 'palette' && !registry.isOpen(id)) armPaletteTypeahead();
  registry.open(id, props);
}

export function closeLayer(id: GlobalLayerId): void {
  registry.close(id);
}

export function toggleLayer<K extends GlobalLayerId>(id: K, props: GlobalLayers[K]): void {
  if (id === 'palette' && !registry.isOpen(id)) armPaletteTypeahead();
  registry.toggle(id, props);
}

export function isLayerOpen(id: GlobalLayerId): boolean {
  return registry.isOpen(id);
}

/** Type helper for `src/overlays/<name>.tsx`: `export default defineGlobalLayer({ id, component })`. */
export function defineGlobalLayer<K extends GlobalLayerId>(
  definition: GlobalLayerDefinition<GlobalLayers, K>,
): GlobalLayerDefinition<GlobalLayers, K> {
  return definition;
}

/** Navigation for fallbacks of reserved ids nobody registered yet (set by the root route). */
export type LayerFallbackNavigate = (to: { pathname: string }) => void;
let fallbackNavigate: LayerFallbackNavigate | null = null;

export function setLayerFallbackNavigate(navigate: LayerFallbackNavigate | null): void {
  fallbackNavigate = navigate;
}

registry.setFallback((request) => {
  const go = (pathname: string) => fallbackNavigate?.({ pathname });
  switch (request.id) {
    case 'record-peek':
      go(`/o/${encodeURIComponent(request.props.objectKey)}/r/${encodeURIComponent(request.props.recordId)}`);
      break;
    case 'record-create':
      go(`/o/${encodeURIComponent(request.props.objectKey)}/new`);
      break;
    case 'presentation': {
      const { source } = request.props;
      // Without the presentation layer a deck opens in its editor (/decks/$id, slides-editor), a page at /pages/$id.
      go(`/${source.kind === 'deck' ? 'decks' : 'pages'}/${encodeURIComponent(source.id)}`);
      break;
    }
    default:
      break;
  }
  // copilot and any other unregistered id: nothing to show yet.
});

/**
 * What an overlay module default-exports: one definition, several, or an effect-only module
 * (`{ Effect }`: an always-mounted light component such as the mobile list substitute).
 */
export type OverlayModule =
  | AnyGlobalLayerDefinition<GlobalLayers>
  | Array<AnyGlobalLayerDefinition<GlobalLayers>>
  | { Effect: ComponentType };

/** Type helper for effect-only overlay modules. */
export function defineOverlayEffect(Effect: ComponentType): { Effect: ComponentType } {
  return { Effect };
}

const registered = new Set<GlobalLayerId>();

/** Register the discovered overlay modules (root route). */
export function registerOverlayModules(modules: OverlayModule[]): void {
  const definitions: Array<AnyGlobalLayerDefinition<GlobalLayers>> = [];
  const effects: ComponentType[] = [];
  for (const m of modules) {
    if (Array.isArray(m)) definitions.push(...m);
    else if ('id' in m) definitions.push(m);
    else effects.push(m.Effect);
  }
  for (const d of definitions) registered.add(d.id);
  registry.define(definitions);
  registry.defineEffects(effects);
}

/** Whether an owner registered the layer (otherwise `openLayer` runs the fallback, if any). */
export function hasGlobalLayer(id: GlobalLayerId): boolean {
  return registered.has(id);
}

/** Renders open global layers and the definitions' effects; mounted once inside LayerRoot. */
export const GlobalLayerHost = registry.Host;

/** ⌘K, the rail search button and the mobile Search tab. */
export function openCommandPalette(): void {
  openLayer('palette', {});
}
